import { zodResolver } from '@hookform/resolvers/zod'
import { DialogClose } from '@radix-ui/react-dialog'
import { ChevronLeft, ChevronRight, Earth } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Button } from '@/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/dialog'
import { ErrorField } from '@/components/error-field'
import { MapStatusBadge } from '@/components/property/monitoring-tab/map-status-badge'
import { Carousel } from '@/components/slider'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/tooltip'
import { WorkerTypeStamp } from '@/components/worker-type-stamp'
import { useAuth } from '@/contexts'
import { ProfileType } from '@/enum/profile-type'
import { useMediaQuery } from '@/hooks/use-media-query'
import { WorkerType } from '@/models/worker'
import {
  monthEvaluationWorkerFormValidationSchema,
  MonthEvaluationWorkerFormValues,
} from '@/schemas/backoffice/month-evaluation-worker'
import api from '@/services/api'

import { DownloadButton } from '../../download-button'
import { ImageOptions, MapCard } from './map-card'
import { MonitoringInfoCard } from './monitoring-info-card'
import { AvailableMonths } from './monitoring-tab'

interface MonthDataResponse {
  mes_referencia: string
  dt_coleta: string
  status_evidencia: 1 | 2 | 3
  imagens: {
    imagem_ndvi_inicial: string
    imagem_ndvi_inicial_leve: string
    imagem_rgb_inicial: string
    imagem_rgb_inicial_leve: string
    imagem_rvi_inicial: string
    imagem_rvi_inicial_leve: string
    imagem_sar_inicial: string
    imagem_sar_inicial_leve: string
    imagem_variacao: string
    imagem_variacao_leve: string
    imagem_variacao_marco_zero: string
    imagem_variacao_marco_zero_leve: string
    imagem_evi_inicial: string
    imagem_evi_inicial_leve: string
    imagem_ndwi_inicial: string
    imagem_ndwi_inicial_leve: string
    imagem_msi_inicial: string
    imagem_msi_inicial_leve: string
    imagem_dpsvi_inicial: string
    imagem_dpsvi_inicial_leve: string
    imagem_dpsvim_inicial: string
    imagem_dpsvim_inicial_leve: string
  }
  indicadores: {
    percentual_preservacao: string
    percentual_preservacao_marco_zero: string
    periodo_comparacao_em_meses: number
    data_comparacao_preservacao: string
  }
  variacao_indicador_preservacao: string // "-0" || "0" || "1"
  variacao_indicador_preservacao_marco_zero: string // "-0" || "0" || "1"
  wk_operacao: string
  worker?: WorkerType
}

interface GetMonthAndYear {
  referenceMonth: string
  previous?: number
}

interface MonthEvaluationProps {
  idProperty: string
  availableMonths: AvailableMonths[] | null
}

interface WorkerSubtypes {
  id: number
  descricao: string
}

interface WorkerConfig {
  nome: string
  tipo: number
  descricao_tipo: string
  sub_tipos: WorkerSubtypes[]
  sub_tipo: number
  dt_cadastro: string
  dt_atualizacao: string
}

interface CurrentMonth {
  date: string
  evidenceId: string
  referenceMonth: string
  evidenceStatus: 1 | 2 | 3
  imagens: {
    RGB?: ImageOptions
    SAR?: ImageOptions
    NDVI?: ImageOptions
    RVI?: ImageOptions
    EVI?: ImageOptions
    NDWI?: ImageOptions
    MSI?: ImageOptions
    DPSVI?: ImageOptions
    DPSVIM?: ImageOptions
  }
  variations: {
    initial: {
      value: string
      variationType: string
      img?: ImageOptions
    }
    period: {
      value: string
      variationType: string
      img?: ImageOptions
      date: string
    }
  }
  worker?: WorkerType
}

export function MonthEvaluation({
  idProperty,
  availableMonths,
}: MonthEvaluationProps) {
  const { register, handleSubmit, formState } =
    useForm<MonthEvaluationWorkerFormValues>({
      resolver: zodResolver(monthEvaluationWorkerFormValidationSchema),
    })

  const [activeIndex, setActiveIndex] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [currentMonth, setCurrentMonth] = useState<CurrentMonth | null>(null)
  const [workerTypes, setWorkerTypes] = useState<WorkerConfig[] | null>(null)
  const [isMonthEvaluationDialogOpen, setMonthEvaluationDialogOpen] =
    useState<boolean>(false)
  const isHD = useMediaQuery('(min-width: 1200px)')

  const { userProfiles, currentProfile } = useAuth()

  const isAdminOrSuperAdmin =
    userProfiles?.includes(ProfileType.Admin) ||
    userProfiles?.includes(ProfileType.SuperAdmin)

  const getMonthAndYear = ({ referenceMonth, previous }: GetMonthAndYear) => {
    const date = new Date(referenceMonth)

    if (previous) {
      date.setUTCMonth(date.getUTCMonth() - previous)
    }

    const period = new Intl.DateTimeFormat('pt-BR', {
      year: 'numeric',
      month: 'long',
      timeZone: 'UTC',
    }).format(date)

    return period.charAt(0).toUpperCase() + period.slice(1)
  }

  useEffect(() => {
    async function getReferenceMonth(referenceMonthId: string) {
      setIsLoading(true)

      await api
        .get<MonthDataResponse>(
          `/property/monitoring/data/${idProperty}/${referenceMonthId}`,
        )
        .then(({ data }) => {
          if (data) {
            setCurrentMonth({
              date: data.dt_coleta,
              referenceMonth: data.mes_referencia,
              evidenceId: referenceMonthId,
              evidenceStatus: data.status_evidencia,
              imagens: {
                RGB: {
                  highRes: data.imagens.imagem_rgb_inicial,
                  lowRes: data.imagens.imagem_rgb_inicial_leve,
                },
                SAR: {
                  highRes: data.imagens.imagem_sar_inicial,
                  lowRes: data.imagens.imagem_sar_inicial_leve,
                },
                NDVI: {
                  highRes: data.imagens.imagem_ndvi_inicial,
                  lowRes: data.imagens.imagem_ndvi_inicial_leve,
                },
                RVI: {
                  highRes: data.imagens.imagem_rvi_inicial,
                  lowRes: data.imagens.imagem_rvi_inicial_leve,
                },
                EVI: {
                  highRes: data.imagens.imagem_evi_inicial,
                  lowRes: data.imagens.imagem_evi_inicial_leve,
                },
                NDWI: {
                  highRes: data.imagens.imagem_ndwi_inicial,
                  lowRes: data.imagens.imagem_ndwi_inicial_leve,
                },
                MSI: {
                  highRes: data.imagens.imagem_msi_inicial,
                  lowRes: data.imagens.imagem_msi_inicial_leve,
                },
                DPSVI: {
                  highRes: data.imagens.imagem_dpsvi_inicial,
                  lowRes: data.imagens.imagem_dpsvi_inicial_leve,
                },
                DPSVIM: {
                  highRes: data.imagens.imagem_dpsvim_inicial,
                  lowRes: data.imagens.imagem_dpsvim_inicial_leve,
                },
              },
              variations: {
                initial: {
                  value: data.indicadores.percentual_preservacao_marco_zero,
                  variationType: data.variacao_indicador_preservacao_marco_zero,
                  img: {
                    highRes: data.imagens.imagem_variacao_marco_zero,
                    lowRes: data.imagens.imagem_variacao_marco_zero_leve,
                  },
                },
                period: {
                  value: data.indicadores.percentual_preservacao,
                  variationType: data.variacao_indicador_preservacao,
                  date: data.indicadores.data_comparacao_preservacao,
                  img: {
                    highRes: data.imagens.imagem_variacao,
                    lowRes: data.imagens.imagem_variacao_leve,
                  },
                },
              },
              worker: data.worker,
            })
          }
        })
        .catch((error) => {
          toast.error(error.response.data.message)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }

    if (availableMonths && availableMonths.length > 0) {
      getReferenceMonth(availableMonths[activeIndex].id_evidencia)
    }
  }, [activeIndex, availableMonths, idProperty])

  useEffect(() => {
    const abortController = new AbortController()
    const signal = abortController.signal

    const isCurrentProfileAdminOrSuperAdmin =
      currentProfile === ProfileType.Admin ||
      currentProfile === ProfileType.SuperAdmin

    async function getWorkerTypes() {
      await api
        .get<WorkerConfig[]>('/worker/configs', { signal })
        .then(({ data }) => setWorkerTypes(data))
        .catch((error) => toast.error(error.response.data.message))
    }

    if (isCurrentProfileAdminOrSuperAdmin) {
      getWorkerTypes()
    }

    return () => {
      abortController.abort()
    }
  }, [currentProfile])

  if (availableMonths === null || currentMonth === null) {
    return (
      <h3 className="text-monochromatic-400 text-base">
        Dados mensais serão gerados depois do primeiro mês
      </h3>
    )
  }

  const monthsEvaluations = availableMonths.map((month) =>
    getMonthAndYear({ referenceMonth: month.mes_referencia }),
  )

  const canGoBack = activeIndex > 0
  const canGoForward = activeIndex < availableMonths.length - 1

  async function handleMonthEvaluationSubmit(
    data: MonthEvaluationWorkerFormValues,
  ) {
    const backObject = {
      worker_subtype_id: Number(data.worker_subtype_id),
    }

    await api
      .post(
        `/worker/preservation/evaluate/reprocess/${currentMonth?.evidenceId}`,
        backObject,
      )
      .then(() => {
        toast.success('Avaliação mensal solicitada com sucesso!')
        setMonthEvaluationDialogOpen(false)
      })
      .catch((error) => {
        toast.error(error.response.data.message)
      })
  }

  return (
    <>
      <div className="flex justify-between">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex w-fit items-center gap-4">
            <Button
              className="bg-monochromatic-200 text-bios-primary disabled:bg-monochromatic-200 disabled:text-monochromatic-300 h-8! w-8! rounded-sm border-none p-0 shadow-md"
              disabled={!canGoBack}
              onClick={() => setActiveIndex((prev) => prev - 1)}
            >
              <ChevronLeft />
            </Button>

            <p className="text-xl">{monthsEvaluations[activeIndex]}</p>

            <Button
              className="bg-monochromatic-200 text-bios-primary disabled:bg-monochromatic-200 disabled:text-monochromatic-300 h-8! w-8! rounded-sm border-none p-0 shadow-md"
              disabled={!canGoForward}
              onClick={() => setActiveIndex((prev) => prev + 1)}
            >
              <ChevronRight />
            </Button>
          </div>

          <div className="-order-1 flex items-center gap-2 sm:order-1">
            <WorkerTypeStamp worker={currentMonth.worker} />

            <MapStatusBadge
              status={currentMonth.evidenceStatus}
              className="w-fit"
            />
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Dialog
            open={isMonthEvaluationDialogOpen}
            onOpenChange={setMonthEvaluationDialogOpen}
          >
            <DialogContent className="h-auto lg:max-w-[35vw]">
              <DialogHeader>
                <DialogTitle>Solicitar avaliação mensal</DialogTitle>
                <DialogDescription>
                  Qual worker você prefere utilizar para a análise?
                </DialogDescription>
              </DialogHeader>

              <form
                className="mx-auto"
                onSubmit={handleSubmit(handleMonthEvaluationSubmit)}
                id="month-evaluation-worker-form"
              >
                {workerTypes &&
                  workerTypes
                    .filter((worker) => worker.tipo === 1)
                    .map((type) => (
                      <div
                        key={type.tipo}
                        className="bg-monochromatic-200 mx-auto flex overflow-hidden rounded-lg shadow-lg"
                      >
                        {type.sub_tipos.map((subtype) => (
                          <div key={subtype.id}>
                            <input
                              id={`worker-type-${type.tipo}-${subtype.id}`}
                              type="radio"
                              defaultChecked={type.sub_tipo === subtype.id}
                              value={subtype.id}
                              className="peer sr-only"
                              {...register('worker_subtype_id')}
                            />

                            <label
                              htmlFor={`worker-type-${type.tipo}-${subtype.id}`}
                              className="not-peer-checked:hover:bg-monochromatic-100 peer-checked:bg-bios-primary text-monochromatic-400 flex cursor-pointer flex-col items-center gap-1 px-5 py-3 text-sm uppercase transition-colors peer-checked:text-white"
                            >
                              {subtype.descricao}
                            </label>
                          </div>
                        ))}
                      </div>
                    ))}
              </form>

              {formState.errors.worker_subtype_id && (
                <ErrorField>
                  {formState.errors.worker_subtype_id.message}
                </ErrorField>
              )}

              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Fechar</Button>
                </DialogClose>

                <Button form="month-evaluation-worker-form" type="submit">
                  Solicitar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {isAdminOrSuperAdmin && !canGoForward && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="tertiary"
                    className="p-3!"
                    onClick={() => setMonthEvaluationDialogOpen(true)}
                  >
                    <Earth />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Pedir avaliação mensal</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          <DownloadButton
            testId="property-monthly-download-monitoring-data-button"
            variant="underline"
            text="Certificado"
            downloadRoute={`/preservation/certificate/monitoring/${currentMonth.evidenceId}`}
          />
        </div>
      </div>
      {Object.keys(currentMonth.imagens || {}).filter(
        (key) =>
          currentMonth.imagens[key as keyof typeof currentMonth.imagens]
            ?.highRes ||
          currentMonth.imagens[key as keyof typeof currentMonth.imagens]
            ?.lowRes,
      ).length > 4 ? (
        <Carousel
          spaceBetween={30}
          itemsPerPage={isHD ? 4 : 1}
          carouselInsideButtons
          centerItems
        >
          {Object.entries(currentMonth.imagens).map(([key, value]) => (
            <MapCard
              key={key}
              mapData={{
                title: key,
                img: value,
                date: currentMonth.date,
                isWithCarousel: true,
                data: currentMonth,
              }}
              isLoading={isLoading}
            />
          ))}
        </Carousel>
      ) : (
        <div className="grid grid-cols-2 gap-7 sm:grid-cols-4">
          {Object.entries(currentMonth.imagens)
            .filter(([, image]) => image?.highRes || image?.lowRes)
            .map(([key, value]) => (
              <MapCard
                key={key}
                mapData={{
                  title: key,
                  img: value,
                  date: currentMonth.date,
                  isWithCarousel: true,
                  data: currentMonth,
                }}
                isLoading={isLoading}
              />
            ))}
        </div>
      )}

      <h3 className="text-2xl">Acompanhar diferenças</h3>

      <div className="grid grid-cols-2 gap-7 sm:grid-cols-4">
        <MapCard
          mapData={{
            img: currentMonth.variations.initial.img,
            title: 'NDVI | Marco zero',
            isWithCarousel: false,
          }}
          isLoading={isLoading}
        />
        <MapCard
          mapData={{
            img: currentMonth.variations.period.img,
            title: `NDVI | Período Anterior: ${getMonthAndYear({
              referenceMonth: currentMonth.variations.period.date,
            })}`,
            isWithCarousel: false,
          }}
          isLoading={isLoading}
        />
      </div>

      <div className="grid grid-cols-1 gap-7 sm:grid-cols-4">
        <MonitoringInfoCard
          className="order-2 sm:order-1"
          data={{
            title: 'Variação em Relação ao Marco 0',
            description: 'Índice de Preservação',
            value: currentMonth.variations.initial.value.replace('.', ','),
            status: currentMonth.variations.initial.variationType,
          }}
          isLoading={isLoading}
        />
        <MonitoringInfoCard
          className="order-3 sm:order-2"
          data={{
            title: 'Variação em Relação ao Período Anterior',
            description: 'Índice de Preservação',
            value: currentMonth.variations.period.value.replace('.', ','),
            status: currentMonth.variations.period.variationType,
          }}
          isLoading={isLoading}
        />
        <Dialog>
          <DialogTrigger asChild>
            <span className="text-alert-success flex cursor-pointer items-center justify-center text-base font-bold underline sm:order-3 sm:items-end sm:justify-start">
              Saiba mais
            </span>
          </DialogTrigger>

          <DialogContent className="lg-py-0 h-auto py-10">
            <DialogDescription className="text-monochromatic-500 space-y-4">
              <p className="lg:paragraph">
                O percentual de preservação da área terá como base inicial, para
                efeito de contrato, a medição do marco zero representada por
                100%. As variações positivas (aumento de vegetação) ou negativas
                (redução de vegetação) serão sempre representadas em relação ao
                marco zero de acordo com a cláusula 5.6 do termo de adesão da
                plataforma 6Bios.
              </p>
            </DialogDescription>
          </DialogContent>
        </Dialog>
      </div>
    </>
  )
}
